import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getTrustComplaint,
  listTrustComplaints,
  updateTrustComplaint,
  type TrustComplaintPayload,
  type TrustComplaintQueue,
  type TrustComplaintStatus,
} from "@/api/trust_complaints";
import {
  EMPTY_TRUST_COMPLAINT_COUNTS,
  type TrustComplaintSourceFilter,
} from "@/components/trust_suite/trustSuiteDisplay";
import { invalidateTrustSuitePipeline } from "@/lib/trustSuiteQueryKeys";

export type TrustTrackStatusFilter = TrustComplaintStatus | "all";
export type TrustTrackQueueFilter = TrustComplaintQueue | "all";

type UseTrustTrackOptions = {
  municipalityId?: number;
  defaultQueue?: TrustTrackQueueFilter;
};

export function useTrustTrack({
  municipalityId,
  defaultQueue = "open",
}: UseTrustTrackOptions) {
  const queryClient = useQueryClient();

  const [queue, setQueueState] = useState<TrustTrackQueueFilter>(defaultQueue);
  const [statusFilter, setStatusFilter] = useState<TrustTrackStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<TrustComplaintSourceFilter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setSelectedId(null);
    setStatusFilter("all");
  }, [municipalityId, debouncedSearch, sourceFilter]);

  const setQueue = (nextQueue: TrustTrackQueueFilter) => {
    setQueueState(nextQueue);
    setStatusFilter("all");
  };

  const focusComplaint = (id: number, nextQueue: TrustComplaintQueue) => {
    setQueue(nextQueue);
    setSelectedId(id);
  };

  const listQueryKey = useMemo(
    () =>
      ["trust_complaints", municipalityId, queue, debouncedSearch, sourceFilter, statusFilter] as const,
    [municipalityId, queue, debouncedSearch, sourceFilter, statusFilter],
  );

  const listQuery = useQuery({
    queryKey: listQueryKey,
    // Keep the last response for this municipality while a new filter combination loads, so the
    // queue and status counts do not drop to zero. Its rows belong to the old filters and are hidden.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === municipalityId ? previousData : undefined,
    queryFn: ({ signal }) =>
      listTrustComplaints(municipalityId!, {
        search: debouncedSearch || undefined,
        source: sourceFilter === "all" ? undefined : sourceFilter,
        queue: queue === "all" ? undefined : queue,
        status: statusFilter === "all" ? undefined : statusFilter,
        signal,
      }),
    enabled: municipalityId != null,
  });

  const complaints = listQuery.isPlaceholderData ? [] : (listQuery.data?.complaints ?? []);
  const counts = listQuery.data?.counts ?? EMPTY_TRUST_COMPLAINT_COUNTS;

  useEffect(() => {
    if (complaints.length === 0) {
      if (selectedId == null) {
        setSelectedId(null);
      }
      return;
    }

    if (selectedId != null && !complaints.some((complaint) => complaint.id === selectedId)) {
      return;
    }

    if (selectedId == null || !complaints.some((complaint) => complaint.id === selectedId)) {
      setSelectedId(complaints[0]?.id ?? null);
    }
  }, [complaints, selectedId]);

  const detailQuery = useQuery({
    queryKey: ["trust_complaint", municipalityId, selectedId],
    queryFn: ({ signal }) => getTrustComplaint(municipalityId!, selectedId!, signal),
    enabled: municipalityId != null && selectedId != null,
  });

  const selected =
    detailQuery.data ?? complaints.find((complaint) => complaint.id === selectedId) ?? null;

  const invalidate = () => {
    if (municipalityId == null) return;
    invalidateTrustSuitePipeline(queryClient, municipalityId);
    queryClient.invalidateQueries({ queryKey: ["trust_complaint", municipalityId] });
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TrustComplaintPayload }) =>
      updateTrustComplaint(municipalityId!, id, patch),
    onSuccess: (updated) => {
      invalidate();
      queryClient.setQueryData(["trust_complaint", municipalityId, updated.id], updated);
    },
  });

  return {
    queue,
    setQueue,
    focusComplaint,
    statusFilter,
    setStatusFilter,
    complaints,
    selected,
    selectedId,
    setSelectedId,
    search,
    setSearch,
    sourceFilter,
    setSourceFilter,
    counts,
    totalCount: listQuery.data?.total_count ?? complaints.length,
    isLoading: listQuery.isLoading || listQuery.isPlaceholderData,
    isDetailLoading: detailQuery.isLoading && detailQuery.fetchStatus !== "idle",
    error: listQuery.error,
    detailError: detailQuery.error,
    updating: updateMutation.isPending,
    updateComplaint: (patch: TrustComplaintPayload) => {
      if (!selected) return;
      updateMutation.mutate({ id: selected.id, patch });
    },
  };
}

export type UseTrustTrackReturn = ReturnType<typeof useTrustTrack>;
